import React from 'react';
import { ChevronDownIcon, ChevronUpIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { CountSeverity } from './CountPanel';
import { PANEL_DRAG_HANDLE_CLASS } from './panelGrid/panelGridTypes';

// =============================================================================
// DashboardTile
// =============================================================================
// Chrome for a movable/collapsible dashboard panel that lives inside a GridStack
// tile (.grid-stack-item-content). Provides a draggable header bar (the title bar
// doubles as the GridStack drag handle) with a collapse chevron, and a body that
// is hidden when collapsed. Presentation-only: the parent owns layout + collapsed
// state and passes the body as children.
//
// Used by the LC dashboard to wrap each count/schedule panel so they can be moved
// around the grid and collapsed to a header-only strip.

export { PANEL_DRAG_HANDLE_CLASS };

// Header bar tones. A collapsed tile hides its contents, so it carries the worst
// severity inside as a colored bar — the icon buttons inherit the bar's text color, and
// their hover wash flips to black on the light yellow bar to stay visible.
const TILE_TONES: Record<'normal' | CountSeverity, { bar: string; hover: string }> = {
  normal: { bar: 'bg-slate-800 text-white', hover: 'hover:bg-white/10' },
  red: { bar: 'bg-red-700 text-white', hover: 'hover:bg-white/10' },
  orange: { bar: 'bg-orange-600 text-white', hover: 'hover:bg-white/10' },
  // Matches the yellow count chip (bg-yellow-400 + black text) — a white-on-yellow bar
  // wouldn't be readable.
  yellow: { bar: 'bg-yellow-400 text-black', hover: 'hover:bg-scrim/10' },
};

export interface DashboardTileProps {
  title: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  locked?: boolean;
  onToggleLock?: () => void;
  /**
   * Roll-up count appended to the title as "Title (n)" while COLLAPSED, so a
   * header-only strip still says how much is inside. Omit (or pass null) when the
   * number isn't known — a collapsed panel skips its query, so a panel that has been
   * collapsed since page load has nothing to report and shows the bare title.
   */
  collapsedCount?: number | null;
  /**
   * Worst severity among the panel's counts, applied to the header bar while
   * COLLAPSED only (expanded, the chips show it themselves).
   */
  collapsedTone?: CountSeverity | null;
  children: React.ReactNode;
}

const DashboardTile: React.FC<DashboardTileProps> = ({ title, collapsed, onToggleCollapse, locked = false, onToggleLock, collapsedCount, collapsedTone, children }) => {
  // Keep header-button clicks from starting a GridStack drag.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const tone = (collapsed && collapsedTone && TILE_TONES[collapsedTone]) || TILE_TONES.normal;
  const heading = collapsed && collapsedCount != null
    ? `${title} (${collapsedCount.toLocaleString()})`
    : title;
  return (
    <div className="h-full w-full flex flex-col rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden bg-panel shadow-sm">
      {/* Header doubles as the GridStack drag handle. The chevron stops propagation
          so clicking it collapses rather than starting a drag. */}
      <div
        className={`${PANEL_DRAG_HANDLE_CLASS} flex items-center gap-2 ${tone.bar} px-2 py-1.5 select-none ${locked ? 'cursor-default' : 'cursor-move'}`}
      >
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          className={`p-0.5 rounded ${tone.hover}`}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />}
        </button>
        <span className="flex-1 text-center font-bold text-sm truncate">{heading}</span>
        {onToggleLock ? (
          <button
            type="button"
            onPointerDown={stop}
            onMouseDown={stop}
            onClick={(e) => { stop(e); onToggleLock(); }}
            className={`p-0.5 rounded ${tone.hover}`}
            title={locked ? 'Unlock panel (allow drag/resize)' : 'Lock panel (prevent drag/resize)'}
          >
            {locked ? <LockClosedIcon className="h-4 w-4" /> : <LockOpenIcon className="h-4 w-4" />}
          </button>
        ) : (
          /* Spacer balances the chevron so the title stays visually centered. */
          <span className="w-5 shrink-0" aria-hidden />
        )}
      </div>
      {!collapsed && <div className="panel-tile-content flex-1 min-h-0 overflow-auto">{children}</div>}
    </div>
  );
};

export default DashboardTile;
